import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsBus,
  AltRoute,
  Person as PersonIcon,
  Assignment,
  Edit as EditIcon,
  DirectionsCar
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { transportAPI, studentAPI } from '../services/api';

const Transport = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [allocations, setAllocations] = useState([]);

  // Dialogs
  const [openVehicleDialog, setOpenVehicleDialog] = useState(false);
  const [openRouteDialog, setOpenRouteDialog] = useState(false);
  const [openDriverDialog, setOpenDriverDialog] = useState(false);
  const [openAllocationDialog, setOpenAllocationDialog] = useState(false);

  // Forms
  const [vehicleForm, setVehicleForm] = useState({ vehicleNumber: '', type: 'Bus', capacity: 30, ownerName: '', ownerPhone: '' });
  const [routeForm, setRouteForm] = useState({ routeName: '', startPoint: '', endPoint: '', fare: '' });
  const [driverForm, setDriverForm] = useState({ firstName: '', lastName: '', licenseNumber: '', phone: '', vehicleId: '' });
  const [allocationForm, setAllocationForm] = useState({ studentId: '', routeId: '', pickupPoint: '' });

  // Helpers for allocation
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        const res = await transportAPI.getVehicles();
        setVehicles(res.data.data);
      } else if (activeTab === 1) {
        const res = await transportAPI.getRoutes();
        setRoutes(res.data.data);
      } else if (activeTab === 2) {
        const [dRes, vRes] = await Promise.all([transportAPI.getDrivers(), transportAPI.getVehicles()]);
        setDrivers(dRes.data.data);
        setVehicles(vRes.data.data); // Needed for assignment
      } else if (activeTab === 3) {
        const [aRes, rRes] = await Promise.all([transportAPI.getAllocations(), transportAPI.getRoutes()]);
        setAllocations(aRes.data.data);
        setRoutes(rRes.data.data); // Needed for assignment
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVehicle = async () => {
    try {
      await transportAPI.createVehicle(vehicleForm);
      toast.success('Vehicle created');
      setOpenVehicleDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create vehicle');
    }
  };

  const handleCreateRoute = async () => {
    try {
      await transportAPI.createRoute(routeForm);
      toast.success('Route created');
      setOpenRouteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create route');
    }
  };

  const handleCreateDriver = async () => {
    try {
      await transportAPI.createDriver(driverForm);
      toast.success('Driver created');
      setOpenDriverDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create driver');
    }
  };

  const handleSearchUser = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setUserResults([]);
      return;
    }
    try {
      const res = await studentAPI.getAll({ search: searchValue });
      const results = res.data.data || [];
      // Frontend filter for exact matching
      const term = searchValue.toLowerCase();
      const filtered = results.filter(u => {
        const firstName = u.user?.firstName?.toLowerCase() || '';
        const lastName = u.user?.lastName?.toLowerCase() || '';
        const admNo = u.admissionNumber?.toLowerCase() || '';
        return firstName.includes(term) || lastName.includes(term) || admNo.includes(term);
      });
      setUserResults(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearchUser(userSearch);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [userSearch]);

  const handleAllocate = async () => {
    if (!selectedStudent || !allocationForm.routeId) return;
    try {
      await transportAPI.allocateTransport({
        studentId: selectedStudent.id,
        routeId: allocationForm.routeId,
        pickupPoint: allocationForm.pickupPoint,
        academicYearId: 1 // Default for now
      });
      toast.success('Transport allocated');
      setOpenAllocationDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Allocation failed');
    }
  };

  const renderVehicles = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenVehicleDialog(true)}>Add Vehicle</Button>
      </Box>
      <Grid container spacing={2}>
        {vehicles.map(v => (
          <Grid item xs={12} sm={6} md={4} key={v.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DirectionsBus color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{v.vehicleNumber}</Typography>
                </Box>
                <Typography color="textSecondary" gutterBottom>{v.type} - {v.capacity} Seats</Typography>
                <Typography variant="body2">Owner: {v.ownerName || 'School Owned'}</Typography>
                <Chip label={v.status} size="small" color={v.status === 'active' ? 'success' : 'default'} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderRoutes = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenRouteDialog(true)}>Add Route</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Start</TableCell><TableCell>End</TableCell><TableCell>Fare</TableCell></TableRow></TableHead>
          <TableBody>
            {routes.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.routeName}</TableCell>
                <TableCell>{r.startPoint}</TableCell>
                <TableCell>{r.endPoint}</TableCell>
                <TableCell>{r.fare}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderDrivers = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenDriverDialog(true)}>Add Driver</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell>License</TableCell><TableCell>Phone</TableCell><TableCell>Vehicle</TableCell></TableRow></TableHead>
          <TableBody>
            {drivers.map(d => (
              <TableRow key={d.id}>
                <TableCell>{d.firstName} {d.lastName}</TableCell>
                <TableCell>{d.licenseNumber}</TableCell>
                <TableCell>{d.phone}</TableCell>
                <TableCell>{d.vehicle?.vehicleNumber || 'Unassigned'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderAllocations = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenAllocationDialog(true)}>Allocate Student</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Student</TableCell><TableCell>Class</TableCell><TableCell>Route</TableCell><TableCell>Pickup</TableCell><TableCell>Fare</TableCell></TableRow></TableHead>
          <TableBody>
            {allocations.map(a => (
              <TableRow key={a.id}>
                <TableCell>{a.student?.firstName} {a.student?.lastName}</TableCell>
                <TableCell>{a.student?.class?.name}</TableCell>
                <TableCell>{a.route?.routeName}</TableCell>
                <TableCell>{a.pickupPoint}</TableCell>
                <TableCell>{a.route?.fare}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Transport Management</Typography>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Vehicles" icon={<DirectionsBus />} iconPosition="start" />
          <Tab label="Routes" icon={<AltRoute />} iconPosition="start" />
          <Tab label="Drivers" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Allocations" icon={<Assignment />} iconPosition="start" />
        </Tabs>
      </Paper>

      {activeTab === 0 && renderVehicles()}
      {activeTab === 1 && renderRoutes()}
      {activeTab === 2 && renderDrivers()}
      {activeTab === 3 && renderAllocations()}

      {/* Vehicle Dialog */}
      <Dialog open={openVehicleDialog} onClose={() => setOpenVehicleDialog(false)}>
        <DialogTitle>Add Vehicle</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Number" margin="normal" value={vehicleForm.vehicleNumber} onChange={e => setVehicleForm({ ...vehicleForm, vehicleNumber: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select value={vehicleForm.type} onChange={e => setVehicleForm({ ...vehicleForm, type: e.target.value })}>
              <MenuItem value="Bus">Bus</MenuItem>
              <MenuItem value="Van">Van</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Capacity" type="number" margin="normal" value={vehicleForm.capacity} onChange={e => setVehicleForm({ ...vehicleForm, capacity: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVehicleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateVehicle}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Route Dialog */}
      <Dialog open={openRouteDialog} onClose={() => setOpenRouteDialog(false)}>
        <DialogTitle>Add Route</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" margin="normal" value={routeForm.routeName} onChange={e => setRouteForm({ ...routeForm, routeName: e.target.value })} />
          <TextField fullWidth label="Start" margin="normal" value={routeForm.startPoint} onChange={e => setRouteForm({ ...routeForm, startPoint: e.target.value })} />
          <TextField fullWidth label="End" margin="normal" value={routeForm.endPoint} onChange={e => setRouteForm({ ...routeForm, endPoint: e.target.value })} />
          <TextField fullWidth label="Fare" type="number" margin="normal" value={routeForm.fare} onChange={e => setRouteForm({ ...routeForm, fare: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRouteDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateRoute}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Driver Dialog */}
      <Dialog open={openDriverDialog} onClose={() => setOpenDriverDialog(false)}>
        <DialogTitle>Add Driver</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="First Name" margin="normal" value={driverForm.firstName} onChange={e => setDriverForm({ ...driverForm, firstName: e.target.value })} />
          <TextField fullWidth label="Last Name" margin="normal" value={driverForm.lastName} onChange={e => setDriverForm({ ...driverForm, lastName: e.target.value })} />
          <TextField fullWidth label="License No" margin="normal" value={driverForm.licenseNumber} onChange={e => setDriverForm({ ...driverForm, licenseNumber: e.target.value })} />
          <TextField fullWidth label="Phone" margin="normal" value={driverForm.phone} onChange={e => setDriverForm({ ...driverForm, phone: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Assign Vehicle</InputLabel>
            <Select value={driverForm.vehicleId} onChange={e => setDriverForm({ ...driverForm, vehicleId: e.target.value })}>
              <MenuItem value="">None</MenuItem>
              {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{v.vehicleNumber}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDriverDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateDriver}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Allocation Dialog */}
      <Dialog open={openAllocationDialog} onClose={() => setOpenAllocationDialog(false)}>
        <DialogTitle>Allocate Transport</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search student (min 2 chars)..."
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
          />
          {userResults.map(u => (
            <MenuItem key={u.id} onClick={() => { setSelectedStudent(u); setUserResults([]); }}>
              {u.firstName} {u.lastName}
            </MenuItem>
          ))}
          {selectedStudent && <Chip label={`Selected: ${selectedStudent.firstName}`} onDelete={() => setSelectedStudent(null)} sx={{ mb: 2 }} />}

          <FormControl fullWidth margin="normal">
            <InputLabel>Route</InputLabel>
            <Select value={allocationForm.routeId} onChange={e => setAllocationForm({ ...allocationForm, routeId: e.target.value })}>
              {routes.map(r => <MenuItem key={r.id} value={r.id}>{r.routeName} ({r.fare})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Pickup Point" margin="normal" value={allocationForm.pickupPoint} onChange={e => setAllocationForm({ ...allocationForm, pickupPoint: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAllocationDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAllocate}>Allocate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transport;
